import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, FormControl, InputLabel, Select, MenuItem,
  Tabs, Tab, CircularProgress, Alert, Chip, TextField,
  IconButton, Tooltip, Divider
} from '@mui/material';
import {
  Refresh, AccountBalance, TrendingUp, CreditCard, Receipt,
  Shield, FilterList, Download, Search, CheckCircle,
  Cancel, HourglassEmpty, DeleteOutline, AddCircleOutline,
  AccountBalanceWallet, NetworkCheck
} from '@mui/icons-material';
import {
  getApiTransactions, getCreditCardTransactions, getBbpsTransactions,
  getWalletBalanceWithLien, getAccountLedger,
  getWhitelistedIps, addWhitelistedIp, removeWhitelistedIp
} from '../../services/api';
import { showLoadingToast, updateToast } from '../../utils/toast';

// ─── helpers ────────────────────────────────────────────────────────────────
const extractArray = (res) => {
  if (!res) return [];
  if (Array.isArray(res.data)) return res.data;
  if (res.data?.content && Array.isArray(res.data.content)) return res.data.content;
  if (res.data?.data && Array.isArray(res.data.data)) return res.data.data;
  return [];
};

const fmt = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount ?? 0);

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Status chip
const StatusChip = ({ status }) => {
  const map = {
    SUCCESS:  { color: '#16a34a', bg: '#dcfce7', icon: <CheckCircle sx={{ fontSize: 12 }} /> },
    FAILED:   { color: '#dc2626', bg: '#fee2e2', icon: <Cancel sx={{ fontSize: 12 }} /> },
    PENDING:  { color: '#d97706', bg: '#fef3c7', icon: <HourglassEmpty sx={{ fontSize: 12 }} /> },
    ACTIVE:   { color: '#16a34a', bg: '#dcfce7', icon: <CheckCircle sx={{ fontSize: 12 }} /> },
    INACTIVE: { color: '#6b7280', bg: '#f3f4f6', icon: <Cancel sx={{ fontSize: 12 }} /> },
  };
  const s = map[status] || { color: '#6b7280', bg: '#f3f4f6', icon: null };
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1.5, py: 0.4, borderRadius: 99,
      backgroundColor: s.bg, color: s.color,
      fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
    }}>
      {s.icon}{status || 'N/A'}
    </Box>
  );
};

// Empty state
const Empty = ({ label }) => (
  <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
    <Receipt sx={{ fontSize: 40, opacity: 0.2, mb: 1 }} />
    <Typography variant="body2">{label}</Typography>
  </Box>
);

// Table wrapper
const DataTable = ({ columns, rows, keyField = 'id' }) => (
  <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: '#f8fafc' }}>
          {columns.map(c => (
            <TableCell key={c.key} sx={{ fontWeight: 700, fontSize: 11, letterSpacing: 0.5, color: '#64748b', textTransform: 'uppercase', py: 1.5 }}>
              {c.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, idx) => (
          <TableRow key={row[keyField] ?? idx} hover sx={{ '&:last-child td': { border: 0 } }}>
            {columns.map(c => (
              <TableCell key={c.key} sx={{ fontSize: 13, py: 1.5, color: '#1e293b' }}>
                {c.render ? c.render(row) : (row[c.key] ?? '—')}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// Tab label
const TabLabel = ({ icon, label, count }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    {icon}
    <span>{label}</span>
    {count != null && (
      <Box sx={{ ml: 0.5, px: 1, py: 0.2, borderRadius: 99, backgroundColor: '#e2e8f0', fontSize: 11, fontWeight: 700, color: '#475569' }}>
        {count}
      </Box>
    )}
  </Box>
);

// ─── main component ──────────────────────────────────────────────────────────
const GatewayReports = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [fromDate, setFromDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [toDate, setToDate]     = useState(new Date().toISOString().split('T')[0]);
  const [apiCode, setApiCode]   = useState('');

  const [transactions, setTransactions]               = useState([]);
  const [creditCardTransactions, setCreditCardTransactions] = useState([]);
  const [bbpsTransactions, setBbpsTransactions]       = useState([]);
  const [balanceData, setBalanceData]                 = useState(null);
  const [ledgerData, setLedgerData]                   = useState([]);
  const [whitelistedIps, setWhitelistedIps]           = useState([]);
  const [newIp, setNewIp]                             = useState('');

  const loaders = [
    loadTransactions, loadCreditCardTransactions, loadBbpsTransactions,
    loadBalanceData, loadLedgerData, loadWhitelistedIps
  ];

  useEffect(() => { loaders[tab]?.(); }, [tab, fromDate, toDate, apiCode]);

  async function loadTransactions() {
    try { setLoading(true); setError(''); setTransactions(extractArray(await getApiTransactions(fromDate, toDate, apiCode))); }
    catch { setError('Failed to load transactions'); setTransactions([]); }
    finally { setLoading(false); }
  }
  async function loadCreditCardTransactions() {
    try { setLoading(true); setError(''); setCreditCardTransactions(extractArray(await getCreditCardTransactions())); }
    catch { setError('Failed to load credit card transactions'); setCreditCardTransactions([]); }
    finally { setLoading(false); }
  }
  async function loadBbpsTransactions() {
    try { setLoading(true); setError(''); setBbpsTransactions(extractArray(await getBbpsTransactions())); }
    catch { setError('Failed to load BBPS transactions'); setBbpsTransactions([]); }
    finally { setLoading(false); }
  }
  async function loadBalanceData() {
    try { setLoading(true); setError(''); const r = await getWalletBalanceWithLien(); setBalanceData(r.data); }
    catch { setError('Failed to load balance data'); setBalanceData(null); }
    finally { setLoading(false); }
  }
  async function loadLedgerData() {
    try { setLoading(true); setError(''); setLedgerData(extractArray(await getAccountLedger())); }
    catch { setError('Failed to load ledger data'); setLedgerData([]); }
    finally { setLoading(false); }
  }
  async function loadWhitelistedIps() {
    try { setLoading(true); setError(''); const r = await getWhitelistedIps(); setWhitelistedIps(r.data || []); }
    catch { setError('Failed to load whitelisted IPs'); setWhitelistedIps([]); }
    finally { setLoading(false); }
  }

  const handleAddIp = async () => {
    if (!newIp.trim()) { setError('Please enter a valid IP address'); return; }
    const tid = showLoadingToast('Adding IP address...');
    try { await addWhitelistedIp({ ipAddress: newIp }); updateToast(tid, 'IP address added', 'success'); setNewIp(''); loadWhitelistedIps(); }
    catch { updateToast(tid, 'Failed to add IP address', 'error'); }
  };

  const handleRemoveIp = async (ipId) => {
    const tid = showLoadingToast('Removing IP address...');
    try { await removeWhitelistedIp(ipId); updateToast(tid, 'IP address removed', 'success'); loadWhitelistedIps(); }
    catch { updateToast(tid, 'Failed to remove IP address', 'error'); }
  };

  // ─── filter bar (shown only for tabs 0–2 & 4) ────────────────────────────
  const showFilters = [0, 1, 2, 4].includes(tab);

  const TABS = [
    { icon: <Receipt sx={{ fontSize: 16 }} />, label: 'Transactions',   count: transactions.length },
    { icon: <CreditCard sx={{ fontSize: 16 }} />, label: 'Credit Card', count: creditCardTransactions.length },
    { icon: <TrendingUp sx={{ fontSize: 16 }} />, label: 'BBPS',        count: bbpsTransactions.length },
    { icon: <AccountBalanceWallet sx={{ fontSize: 16 }} />, label: 'Balance' },
    { icon: <AccountBalance sx={{ fontSize: 16 }} />, label: 'Ledger',  count: ledgerData.length },
    { icon: <NetworkCheck sx={{ fontSize: 16 }} />, label: 'IP Whitelist', count: whitelistedIps.length },
  ];

  return (
    <Box sx={{ width: '100%', p: { xs: 2, md: 3 } }}>

      {/* ── Page Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>Gateway Reports</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Transactions, balances, ledger & IP management
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton
            onClick={() => loaders[tab]?.()}
            disabled={loading}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
          >
            <Refresh sx={{ fontSize: 18, ...(loading && { animation: 'spin 1s linear infinite' }) }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Filter Bar ── */}
      {showFilters && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ py: '14px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <FilterList sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>Filters</Typography>
            </Box>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>From Date</Typography>
                <input
                  type="date"
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  style={{
                    width: '100%', padding: '9px 12px',
                    border: '1px solid #e2e8f0', borderRadius: 8,
                    fontSize: 13, color: '#1e293b', outline: 'none',
                    fontFamily: 'inherit', backgroundColor: '#fff',
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>To Date</Typography>
                <input
                  type="date"
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  style={{
                    width: '100%', padding: '9px 12px',
                    border: '1px solid #e2e8f0', borderRadius: 8,
                    fontSize: 13, color: '#1e293b', outline: 'none',
                    fontFamily: 'inherit', backgroundColor: '#fff',
                  }}
                />
              </Grid>
              {tab === 0 && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>API Code</Typography>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={apiCode}
                      onChange={e => setApiCode(e.target.value)}
                      displayEmpty
                      sx={{ borderRadius: 2, fontSize: 13 }}
                    >
                      <MenuItem value="">All APIs</MenuItem>
                      <MenuItem value="UPI">UPI</MenuItem>
                      <MenuItem value="PAYTM">Paytm</MenuItem>
                      <MenuItem value="BBPS">BBPS</MenuItem>
                      <MenuItem value="DMT">DMT</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={tab === 0 ? 3 : 6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Search sx={{ fontSize: 15 }} />}
                    onClick={() => loaders[tab]?.()}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 2.5, boxShadow: 'none' }}
                  >
                    Apply
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setFromDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
                      setToDate(new Date().toISOString().split('T')[0]);
                      setApiCode('');
                    }}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    Reset
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* ── Tabs ── */}
      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 48, px: 2 },
            '& .Mui-selected': { color: '#2563eb' },
            '& .MuiTabs-indicator': { backgroundColor: '#2563eb', height: 3, borderRadius: 2 },
          }}
        >
          {TABS.map((t, i) => (
            <Tab key={i} label={<TabLabel icon={t.icon} label={t.label} count={t.count} />} />
          ))}
        </Tabs>
      </Box>

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Loading overlay */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {/* ── Tab: Transactions ── */}
      {tab === 0 && !loading && (
        <DataTable
          rows={transactions}
          columns={[
            { key: 'createdAt', label: 'Date', render: r => fmtDate(r.createdAt) },
            { key: 'apiCode', label: 'API Code', render: r => <Chip label={r.apiCode || 'N/A'} size="small" sx={{ fontSize: 11, fontWeight: 600 }} /> },
            { key: 'transactionId', label: 'Transaction ID', render: r => <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{r.transactionId || '—'}</Typography> },
            { key: 'type', label: 'Type' },
            { key: 'amount', label: 'Amount', render: r => <Typography sx={{ fontWeight: 700, color: r.status === 'SUCCESS' ? '#16a34a' : '#dc2626' }}>{fmt(r.amount)}</Typography> },
            { key: 'status', label: 'Status', render: r => <StatusChip status={r.status} /> },
          ]}
        />
      )}
      {tab === 0 && !loading && transactions.length === 0 && <Empty label="No transactions found for the selected filters" />}

      {/* ── Tab: Credit Card ── */}
      {tab === 1 && !loading && (
        <DataTable
          rows={creditCardTransactions}
          columns={[
            { key: 'createdAt', label: 'Date', render: r => fmtDate(r.createdAt) },
            { key: 'last4Digits', label: 'Card', render: r => <Typography sx={{ fontFamily: 'monospace', letterSpacing: 2 }}>••••&nbsp;{r.last4Digits}</Typography> },
            { key: 'amount', label: 'Amount', render: r => <Typography sx={{ fontWeight: 700, color: r.status === 'SUCCESS' ? '#16a34a' : '#dc2626' }}>{fmt(r.amount)}</Typography> },
            { key: 'status', label: 'Status', render: r => <StatusChip status={r.status} /> },
          ]}
        />
      )}
      {tab === 1 && !loading && creditCardTransactions.length === 0 && <Empty label="No credit card transactions found" />}

      {/* ── Tab: BBPS ── */}
      {tab === 2 && !loading && (
        <DataTable
          rows={bbpsTransactions}
          columns={[
            { key: 'createdAt', label: 'Date', render: r => fmtDate(r.createdAt) },
            { key: 'transactionId', label: 'Transaction ID', render: r => <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{r.transactionId || '—'}</Typography> },
            { key: 'amount', label: 'Amount', render: r => <Typography sx={{ fontWeight: 700, color: r.status === 'SUCCESS' ? '#16a34a' : '#dc2626' }}>{fmt(r.amount)}</Typography> },
            { key: 'status', label: 'Status', render: r => <StatusChip status={r.status} /> },
          ]}
        />
      )}
      {tab === 2 && !loading && bbpsTransactions.length === 0 && <Empty label="No BBPS transactions found" />}

      {/* ── Tab: Balance ── */}
      {tab === 3 && !loading && (
        balanceData ? (
          <Grid container spacing={3}>
            {[
              { label: 'Available Balance', value: balanceData.availableBalance, color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
              { label: 'Lien Amount',       value: balanceData.lienAmount,       color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
              { label: 'Total Balance',     value: (balanceData.availableBalance ?? 0) + (balanceData.lienAmount ?? 0), color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
            ].map(b => (
              <Grid item xs={12} md={4} key={b.label}>
                <Box sx={{ p: 3, borderRadius: 3, backgroundColor: b.bg, border: '1.5px solid', borderColor: b.border }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: b.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>{b.label}</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: b.color, mt: 1 }}>{fmt(b.value)}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : <Empty label="No balance data available" />
      )}

      {/* ── Tab: Account Ledger ── */}
      {tab === 4 && !loading && (
        <DataTable
          rows={ledgerData}
          columns={[
            { key: 'createdAt', label: 'Date', render: r => fmtDateTime(r.createdAt) },
            { key: 'description', label: 'Description' },
            { key: 'credit', label: 'Credit', render: r => r.credit ? <Typography sx={{ fontWeight: 700, color: '#16a34a' }}>{fmt(r.credit)}</Typography> : '—' },
            { key: 'debit', label: 'Debit',  render: r => r.debit  ? <Typography sx={{ fontWeight: 700, color: '#dc2626' }}>{fmt(r.debit)}</Typography>  : '—' },
            { key: 'balance', label: 'Balance', render: r => <Typography sx={{ fontWeight: 700, color: (r.balance ?? 0) >= 0 ? '#16a34a' : '#dc2626' }}>{fmt(r.balance)}</Typography> },
          ]}
        />
      )}
      {tab === 4 && !loading && ledgerData.length === 0 && <Empty label="No ledger entries found" />}

      {/* ── Tab: IP Whitelist ── */}
      {tab === 5 && !loading && (
        <Box>
          {/* Add IP */}
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Add New IP Address</Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="e.g. 192.168.1.1"
                  value={newIp}
                  onChange={e => setNewIp(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddIp()}
                  style={{
                    flex: 1, padding: '9px 12px', border: '1px solid #e2e8f0',
                    borderRadius: 8, fontSize: 13, fontFamily: 'monospace',
                    color: '#1e293b', outline: 'none',
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddCircleOutline sx={{ fontSize: 16 }} />}
                  onClick={handleAddIp}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 'none', whiteSpace: 'nowrap' }}
                >
                  Add IP
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* IP List */}
          {whitelistedIps.length === 0 ? (
            <Empty label="No IP addresses whitelisted" />
          ) : (
            <DataTable
              rows={whitelistedIps}
              columns={[
                { key: 'ipAddress', label: 'IP Address', render: r => <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.ipAddress}</Typography> },
                { key: 'createdAt', label: 'Added Date', render: r => fmtDate(r.createdAt) },
                { key: 'status', label: 'Status', render: r => <StatusChip status={r.status} /> },
                { key: 'actions', label: 'Action', render: r => (
                  <Tooltip title="Remove IP">
                    <IconButton size="small" color="error" onClick={() => handleRemoveIp(r.id)}>
                      <DeleteOutline sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )},
              ]}
            />
          )}
        </Box>
      )}

    </Box>
  );
};

export default GatewayReports;